import React from "react";
import { useHistory } from "react-router";
import { ToggleItems } from "../../components";
import { Modal } from "../../components/Modal/Modal";
import { Space } from "../../components/Space/Space";

import { useAPI } from "../../providers/ApiProvider";
import { cn } from "../../utils/bem";
import { ConfigPage } from "./Config/Config";
import "./CreateProject.scss";
import { ImportPage } from "./Import/Import";
import { useImportPage } from "./Import/useImportPage";
import { useDraftProject } from "./utils/useDraftProject";
import { Input, TextArea } from "../../components/Form";

import { SecurityDeposit } from "./SecurityDeposit/SecurityDeposit";
import { ExpertiseSelector } from "../../components/ExpertiseSelector";

const primaryButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  padding: "0 16px",
  height: "40px",
  minWidth: "90px",
  background: "#8b5cf6",
  border: "1px solid #8b5cf6",
  color: "#ffffff",
  fontSize: "13px",
  fontWeight: 600,
  fontFamily: "'Space Grotesk', system-ui, sans-serif",
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const dangerButtonStyle = {
  ...primaryButtonStyle,
  background: "rgba(239, 68, 68, 0.12)",
  border: "1px solid rgba(239, 68, 68, 0.3)",
  color: "#fca5a5",
};

const successButtonStyle = {
  ...primaryButtonStyle,
  background: "#10b981",
  borderColor: "#10b981",
  color: "#ffffff",
};

const ProjectName = ({
  name,
  setName,
  onSaveName,
  error,
  description,
  setDescription,
  selectedCategoryId,
  setSelectedCategoryId,
  selectedSpecializationId,
  setSelectedSpecializationId,
  show = true,
}) =>
  !show ? null : (
    <form className={cn("project-name")}>
      <div className="w-full flex flex-col gap-2">
        <label className="w-full" htmlFor="project_name">
          Project Name
        </label>
        <Input
          name="name"
          id="project_name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={onSaveName}
          placeholder="Enter project name"
          className="project-title w-full"
          autoFocus
        />
        {error && <span className="-mt-1 text-negative-content">{error}</span>}
      </div>
      <div className="w-full flex flex-col gap-2">
        <label className="w-full" htmlFor="project_description">
          Description
        </label>
        <TextArea
          name="description"
          id="project_description"
          placeholder="Optional description of your project"
          rows="4"
          style={{ minHeight: 100 }}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={onSaveName}
          className="project-description w-full"
        />
      </div>
      <ExpertiseSelector
        categoryId={selectedCategoryId}
        specializationId={selectedSpecializationId}
        onCategoryChange={setSelectedCategoryId}
        onSpecializationChange={setSelectedSpecializationId}
      />
    </form>
  );

export const CreateProject = ({ onClose }) => {
  const [step, _setStep] = React.useState("name"); // name | import | config | deposit
  const [waiting, setWaitingStatus] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const { project, setProject: updateProject } = useDraftProject();
  const history = useHistory();
  const api = useAPI();

  const [name, setName] = React.useState("");
  const [error, setError] = React.useState();
  const [description, setDescription] = React.useState("");
  const [sample, setSample] = React.useState(null);
  const [depositPaid, setDepositPaid] = React.useState(false);

  // Expertise requirements state - expertise_required is derived from whether a category is selected
  const [selectedCategoryId, setSelectedCategoryId] = React.useState(null);
  const [selectedSpecializationId, setSelectedSpecializationId] = React.useState(null);

  // Translation language pair state — only set when Machine Translation template is chosen
  const [sourceLanguage, setSourceLanguage] = React.useState("");
  const [targetLanguage, setTargetLanguage] = React.useState("");

  const setStep = React.useCallback((step) => {
    _setStep(step);
    const eventNameMap = {
      name: "project_name",
      import: "data_import",
      config: "labeling_setup",
      deposit: "security_deposit",
    };
    __ca(`create_project.tab.${eventNameMap[step]}`);
  }, []);

  const [hasDicom, setHasDicom] = React.useState(false);
  const [detectedFileType, setDetectedFileType] = React.useState(null);
  const [estimatedTaskCount, setEstimatedTaskCount] = React.useState(0);

  React.useEffect(() => {
    setError(null);
  }, [name]);

  const { columns, uploading, uploadDisabled, finishUpload, fileIds, pageProps, uploadSample } = useImportPage(
    project,
    sample,
  );

  const rootClass = cn("create-project");
  const tabClass = rootClass.elem("tab");

  // Pre-calculate valid states for rendering (duplicated logic for render scope, or move logic up?)
  // Better to move logic up.
  // Wait, I can't access isNameValid here because it's defined later.
  // I need to reorganize the component slightly or define the checks earlier.
  // ... Or just inline the checks here.

  const _isNameValid = !!name && !error;
  const _isImportValid = _isNameValid && (fileIds?.length > 0 || (project?.task_number && project.task_number > 0));
  const _isConfigValid = _isImportValid && !!project?.label_config && project.label_config !== "<View></View>";

  const steps = {
    name: <span className={tabClass.mod({ disabled: !!error })}>Project Name</span>,
    import: <span className={tabClass.mod({ disabled: !_isNameValid })}>Data Import</span>,
    config: <span className={tabClass.mod({ disabled: !_isImportValid })}>Labeling Setup</span>,
    deposit: <span className={tabClass.mod({ disabled: !_isConfigValid })}>Project Expenditure</span>,
  };

  // Handle deposit collection
  const handleDepositCollected = React.useCallback((response) => {
    setDepositPaid(true);
    console.log("Project expenditure collected:", response);
  }, []);

  const handleDepositError = React.useCallback((errorMsg) => {
    console.error("Deposit error:", errorMsg);
    // Could show a toast or modal here
  }, []);

  // name intentionally skipped from deps:
  // this should trigger only once when we got project loaded
  React.useEffect(() => {
    project && !name && setName(project.title);
  }, [project]);

  const projectBody = React.useMemo(
    () => ({
      title: name,
      description,
      label_config: project?.label_config ?? "<View></View>",
      expertise_required: selectedCategoryId !== null,
      required_expertise_category: selectedCategoryId,
      required_expertise_specialization: selectedSpecializationId,
      source_language: sourceLanguage,
      target_language: targetLanguage,
    }),
    [
      name,
      description,
      project?.label_config,
      selectedCategoryId,
      selectedSpecializationId,
      sourceLanguage,
      targetLanguage,
    ],
  );

  const onCreate = React.useCallback(async () => {
    if (submitting) return; // Prevent multiple submissions

    setSubmitting(true);

    try {
      // First, persist project with label_config so import/reimport validates against it
      const response = await api.callApi("updateProject", {
        params: {
          pk: project.id,
        },
        body: projectBody,
      });

      if (response === null) {
        setSubmitting(false);
        return;
      }

      const imported = await finishUpload();

      if (!imported) {
        if (window.__ca) {
          window.__ca("project.create.config.view");
        }
        setSubmitting(false);
        return;
      }

      setWaitingStatus(true);

      if (sample) await uploadSample(sample);

      __ca("create_project.create", { sample: sample?.url });

      setWaitingStatus(false);

      history.push(`/projects/${response.id}/data`);
    } catch (error) {
      console.error("Error creating project:", error);
      setSubmitting(false);
      setWaitingStatus(false);
    }
  }, [project, projectBody, finishUpload, submitting]);

  const onSaveName = async () => {
    if (error) return;

    // If project doesn't exist yet, create it
    if (!project?.id) {
      if (!name) return;

      if (window.__ca) {
        window.__ca("project.create.view");
      }
      setWaitingStatus(true);
      const res = await api.callApi("createProject", {
        body: {
          title: name,
          description,
          expertise_required: selectedCategoryId !== null,
          required_expertise_category: selectedCategoryId,
          required_expertise_specialization: selectedSpecializationId,
        },
      });
      setWaitingStatus(false);

      if (!res) {
        setError("Failed to create project");
        return;
      }

      if (!res.id) {
        setError(res.validation_errors?.title || "Failed to create project");
        return;
      }

      // Set the newly created project
      updateProject(res);
      __ca("create_project.project_created", { id: res.id });
      return;
    }

    // Otherwise, update existing project
    const res = await api.callApi("updateProjectRaw", {
      params: {
        pk: project.id,
      },
      body: {
        title: name,
        description,
        expertise_required: selectedCategoryId !== null,
        required_expertise_category: selectedCategoryId,
        required_expertise_specialization: selectedSpecializationId,
      },
    });

    if (res.ok) return;
    const err = await res.json();

    setError(err.validation_errors?.title);
  };

  const onDelete = React.useCallback(() => {
    const performClose = async () => {
      setWaitingStatus(true);
      if (project?.id)
        await api.callApi("deleteProject", {
          params: {
            pk: project.id,
          },
        });
      setWaitingStatus(false);
      updateProject(null);
      onClose?.();
    };
    performClose();
  }, [project]);

  // Get estimated task count from uploads (including ZIP file contents)
  const estimatedTasks = React.useMemo(() => {
    // Use estimatedTaskCount from Import component (includes ZIP contents analysis)
    // Falls back to fileIds length or project task_number
    return estimatedTaskCount || fileIds?.length || project?.task_number || 0;
  }, [estimatedTaskCount, fileIds, project?.task_number]);

  const isDisabled = !project || uploadDisabled || !!error || !depositPaid || submitting;

  // Validation Logic for Steps
  const isNameValid = !!name && !error;
  const isImportValid = isNameValid && (fileIds?.length > 0 || (project?.task_number && project.task_number > 0));
  const isConfigValid = isImportValid && !!project?.label_config && project.label_config !== "<View></View>";

  // Custom setStep to enforce sequential navigation
  const handleSetStep = React.useCallback(
    (newStep) => {
      if (newStep === "import" && !isNameValid) return;
      if (newStep === "config" && !isImportValid) return;
      if (newStep === "deposit" && !isConfigValid) return;

      setStep(newStep);
    },
    [isNameValid, isImportValid, isConfigValid, setStep],
  );

  const saveButtonStyle = isDisabled
    ? {
        ...primaryButtonStyle,
        background: "rgba(55, 65, 81, 0.5)",
        borderColor: "rgba(55, 65, 81, 0.5)",
        color: "#6b7280",
        cursor: "not-allowed",
      }
    : primaryButtonStyle;

  return (
    <Modal onHide={onDelete} closeOnClickOutside={false} allowToInterceptEscape fullscreen visible bare>
      <div className={rootClass}>
        <Modal.Header>
          <h1>Create Project</h1>
          <ToggleItems items={steps} active={step} onSelect={handleSetStep} />

          <Space>
            <button
              style={dangerButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)";
                e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(239, 68, 68, 0.12)";
                e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.3)";
              }}
              look="outlined"
              onClick={onDelete}
              waiting={waiting}
              aria-label="Cancel project creation"
            >
              Cancel
            </button>
            <button
              look="primary"
              onClick={onCreate}
              style={saveButtonStyle}
              onMouseEnter={(e) => {
                if (!isDisabled) e.currentTarget.style.background = "#7c3aed";
              }}
              onMouseLeave={(e) => {
                if (!isDisabled) e.currentTarget.style.background = "#8b5cf6";
              }}
              waiting={waiting || uploading || submitting}
              waitingClickable={false}
              disabled={isDisabled}
            >
              {submitting ? "Creating..." : "Save"}
            </button>
          </Space>
        </Modal.Header>
        <ProjectName
          name={name}
          setName={setName}
          error={error}
          onSaveName={onSaveName}
          description={description}
          setDescription={setDescription}
          selectedCategoryId={selectedCategoryId}
          setSelectedCategoryId={setSelectedCategoryId}
          selectedSpecializationId={selectedSpecializationId}
          setSelectedSpecializationId={setSelectedSpecializationId}
          show={step === "name"}
        />
        <ImportPage
          project={project}
          show={step === "import"}
          sample={sample}
          onSampleDatasetSelect={setSample}
          openLabelingConfig={() => setStep("config")}
          onDicomDetected={setHasDicom}
          onTypeDetected={setDetectedFileType}
          onEstimatedTasksUpdate={setEstimatedTaskCount}
          {...pageProps}
        />
        <ConfigPage
          project={project}
          onUpdate={(config) => {
            updateProject({ ...project, label_config: config });
          }}
          show={step === "config"}
          columns={columns}
          disableSaveButton={true}
          hasDicom={hasDicom}
          detectedFileType={detectedFileType}
          onLanguagePairChange={({ sourceCode, targetCode }) => {
            setSourceLanguage(sourceCode);
            setTargetLanguage(targetCode);
          }}
        />
        <SecurityDeposit
          project={project}
          estimatedTasks={estimatedTasks}
          estimatedStorageGB={0}
          onDepositCollected={handleDepositCollected}
          onError={handleDepositError}
          show={step === "deposit"}
        />
      </div>
    </Modal>
  );
};
